import React, { useState, useContext, useEffect } from 'react';
import { Card, NavDropdown, ButtonGroup, Button, Form, Dropdown  } from 'react-bootstrap';
import CustomDropdown from './CustomDropdown';
import Chart from 'react-apexcharts';
import Popover from '@mui/material/Popover';
import { useLocation } from 'react-router-dom';
import { useWidgetContext } from '../context/DragContext';
import { AuthContext } from '../context/AuthContext';
import SwitchAccount from '../common/SwitchAccount';

const Performance = () => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const location = useLocation(); // Get the current location
    const { removeWidgetFromGrid } = useWidgetContext();
    const [activeButton, setActiveButton] = useState('1D');
    const { preformanceData, perfomanceChartData, userData, thirdParyAccount } = useContext(AuthContext);
    const [chartData, setChartData] = useState({ categories: [], series: [] });
    const [switchAccount, setSwitchAccount] = useState(false);

    const handleOpenSwitchAccount = () => setSwitchAccount(true);
    const handleCloseSwitchAccount = () => setSwitchAccount(false);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const getStartDateAndEndDate = (buttonLabel) => {
        const currentDate = new Date();
        let startDate, endDate;
    
        // Reset the time to midnight for the date comparison
        currentDate.setHours(0, 0, 0, 0);
    
        switch (buttonLabel) {
            case '1D':
                // Subtract 1 day for 1D
                startDate = new Date(currentDate);
                startDate.setDate(currentDate.getDate() - 1);
                endDate = currentDate;
                break;
            case '5D':
                // Subtract 5 days for 5D
                startDate = new Date(currentDate);
                startDate.setDate(currentDate.getDate() - 5);
                endDate = currentDate;
                break;
            case '1M':
                // Subtract 1 month for 1M
                startDate = new Date(currentDate);
                startDate.setMonth(currentDate.getMonth() - 1);
                endDate = currentDate;
                break;
            case '6M':
                // Subtract 6 months for 6M
                startDate = new Date(currentDate);
                startDate.setMonth(currentDate.getMonth() - 6);
                endDate = currentDate;
                break;
            case 'YTD':
                // Start of current year for YTD
                startDate = new Date(currentDate.getFullYear(), 0, 1);
                endDate = currentDate;
                break;
            case '5Y':
                // Subtract 5 years for 5Y
                startDate = new Date(currentDate);
                startDate.setFullYear(currentDate.getFullYear() - 5);
                endDate = currentDate;
                break;
            case '10Y':
                // Subtract 10 years for 10Y
                startDate = new Date(currentDate);
                startDate.setFullYear(currentDate.getFullYear() - 10);
                endDate = currentDate;
                break;
            default:
                startDate = endDate = currentDate;
        }
    
        // Format the date in YYYY-MM-DD format
        const formatDate = (date) => {
            return date.toISOString().split('T')[0];  // Extract date part (YYYY-MM-DD)
        };
    
        return {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate)
        };
    };

    useEffect(() => {
        const { startDate, endDate } = getStartDateAndEndDate(activeButton);
        preformanceData(thirdParyAccount, userData?.integrations?.trade_account_id,activeButton,startDate, endDate )
    }, [activeButton, preformanceData]);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleButtonClick = (buttonLabel) => {
        setActiveButton(buttonLabel);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const [showDropdown, setShowDropdown] = useState(false);
    const [chartType, setChartType] = useState('line'); // 'line' or 'bar'
    const [chartOption, setChartOption] = useState('Line Chart');
    const [selectedSeries, setSelectedSeries] = useState([true, true, true, false, false]); // Toggle state for each series
    const [selectedDropdownOption, setSelectedDropdownOption] = useState('1M – 11M');

    const handleToggle = (isOpen) => {
        setShowDropdown(isOpen);
    };

    const handleSvgClick = () => {
        setShowDropdown(!showDropdown);
    };

    const handleDropdownSelect = (eventKey) => {
        setSelectedDropdownOption(eventKey); 
        setActiveButton(eventKey); // Set active state to the selected value
        console.log(eventKey);
    };

    const handleChartChange = (option) => {
        setChartOption(option);
        setChartType(option === 'Line Chart' ? 'line' : 'bar');
    };

    const handleSeriesToggle = (index) => {
        const updatedSelectedSeries = [...selectedSeries];
        updatedSelectedSeries[index] = !updatedSelectedSeries[index];
        setSelectedSeries(updatedSelectedSeries);
    };

    const getRandomColor = (name) => {
        const colorMapping = {
        "GC": "#008ffb",
        "SI": "#ff4560",
        "ZC": "#F4F1ED",
        "AAPL": "#FF5733",
        "Core" : "#8A2BE2",
        "CL": "#775dd0",
        "ZS": "#feb019",
        // Add more colors for other assets here
        };
        return colorMapping[name] || '#000000'; // Default color if no match
    };

    // Chart data and options for ApexCharts
    const chartOptions = {
    chart: {
      type: chartType,
      height: 350,
      toolbar: {
        show: true,
        tools: {
          download: false,
          pan: false,
          zoom: false,
          zoomin: true,
          zoomout: true,
          reset: true,
        },
      },
      zoom: {
        enabled: true,
        type: 'x', // horizontal zoom
        autoScaleYaxis: true,
      },
    },
    xaxis: {
      type: 'datetime',
      categories: chartData.categories || [],
      labels: {
        style: { colors: '#A8A8A8' },
      },
      tickAmount: 3, // Show only ~2–3 ticks per frame
      scrollbar: {
        enabled: true,
        height: 8,
        offsetY: 10,
      },
    },
    yaxis: {
      opposite: true,
      labels: {
        style: { colors: '#A8A8A8' },
        formatter: (value) => value.toFixed(2) + '%',
      },
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    grid: {
      borderColor: '#555',
    },
    legend: {
      show: false,
    },
    tooltip: {
      theme: 'dark',
    },
  }

    const pnlResults = perfomanceChartData;

    // Parse and transform data for the chart
    useEffect(() => {
        const results = pnlResults?.pnl_results || {};
    
        // Check if we have valid data to process
        if (!results || Object.keys(results).length === 0) {
            setChartData({
                categories: [],
                series: []
            });
            setSelectedSeries([]);
            return;
        }
    
        // Extract unique timestamps and sort them, ignoring 'Core'
        const timestamps = [
            ...new Set(
                Object.keys(results)
                    .filter(symbol => symbol !== 'Core') // Exclude 'Core'
                    .flatMap(symbol => results[symbol].map(data => data.timestamp))
                    .filter(timestamp => timestamp && !isNaN(new Date(timestamp).getTime())) // Filter out invalid timestamps
            )
        ].sort();
    
        // If no valid timestamps, set empty data
        if (timestamps.length === 0) {
            setChartData({
                categories: [],
                series: []
            });
            setSelectedSeries([]);
            return;
        }
    
        // Map data to series format
        const seriesData = Object.keys(results)
      .filter((symbol) => symbol !== 'Core') // Exclude 'Core'
      .map((symbol) => {
        const data = results[symbol] || []
        let lastVal = null

        const dataPoints = timestamps.map((ts) => {
            const entry = data.find((d) => d.timestamp === ts)
            if (entry) {
                lastVal = entry.pnl
                return lastVal
            }
            // carry forward last known value
            return lastVal // Default to 0 if no data for the timestamp
            })

        return {
            name: symbol, // Use the symbol as the series name
            data: dataPoints,
            color: getRandomColor(symbol), // Assign color based on the symbol
            }
        })
    
        // Convert timestamps to valid Date objects for ApexCharts
        const validCategories = timestamps.map(ts => {
            const date = new Date(ts);
            return isNaN(date.getTime()) ? new Date() : date;
        });

        setChartData({
            categories: validCategories,
            series: seriesData
        });
    
        // Initialize all series toggles to true
        setSelectedSeries(new Array(seriesData.length).fill(true));
    }, [pnlResults]);
    
    
    // const seriesData = [
    //     { name: 'GCQ4', data: [10, 12, 10, 15, 20], color: '#008ffb' },
    //     { name: 'CLQ4', data: [10, 10, 5, 10, -5], color: '#00e396' },
    //     { name: 'ZSN4', data: [10, 4, 8, 12, 16], color: '#feb019' },
    //     { name: 'SIN4', data: [10, 2, 15, 10, 16], color: '#ff4560' },
    //     { name: 'GCQ5', data: [10, 7, 5, 13, 17], color: '#775dd0' }
    // ];

    // Filter the series based on the selected toggles
    // const filteredSeries = seriesData.filter((_, index) => selectedSeries[index]);
    const filteredSeries = (chartData.series || []).filter((_, index) => selectedSeries[index]);

    // Function to determine if the current route contains '/Home/AI'
    const isAIPage = location.pathname.includes('/Home/AI');

    // Function to get the header name based on the current route
    const getHeaderName = () => {
        switch (location.pathname) {
            case '/Home/Portfolio':
                return 'Performance';
            case '/Home/AI':
                return 'Ranking of Positions';
            default:
                return 'Performance';
        }
    };

    return (
        <>
            <Card className={`main-card mt-3 card-large ${ userData?.added_integrations?.length === 0 ? 'blur-card' : ''}`}>
                { userData?.added_integrations?.length === 0 && (
                    <div className="blur-overlay">
                    <img
                        src={`${process.env.PUBLIC_URL}/images/lock-icon.svg`}
                        alt="Lock Icon"
                        style={{ height: '20px', width: '16px' }}
                        className="lock-icon"
                    />
                    {userData?.added_integrations?.length === 0 && <button className="subscribe-button" onClick={handleOpenSwitchAccount}>Click to add Provider</button>}
                    </div>
                )}
                <Card.Header className="pb-0">
                    <div className="d-flex justify-content-between">
                        {isAIPage ? (
                            <div className="d-flex align-items-center">
                                <h2 className="medium-heading mb-0">{getHeaderName()}</h2>
                                <div className='ai_block'>
                                    <span className="ml-2">AI</span>
                                </div>
                            </div>
                        ) : (
                            <h2 className="medium-heading mb-0">{getHeaderName()}</h2>
                        )}

                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                onClick={handleSvgClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <path d="M12 13.5C11.7033 13.5 11.4133 13.412 11.1666 13.2472C10.92 13.0824 10.7277 12.8481 10.6142 12.574C10.5006 12.2999 10.4709 11.9983 10.5288 11.7074C10.5867 11.4164 10.7296 11.1491 10.9393 10.9393C11.1491 10.7296 11.4164 10.5867 11.7074 10.5288C11.9983 10.4709 12.2999 10.5006 12.574 10.6142C12.8481 10.7277 13.0824 10.92 13.2472 11.1666C13.412 11.4133 13.5 11.7033 13.5 12C13.5 12.3978 13.342 12.7794 13.0607 13.0607C12.7794 13.342 12.3978 13.5 12 13.5Z" fill="white" />
                                <path d="M18.75 13.5C18.4533 13.5 18.1633 13.412 17.9166 13.2472C17.67 13.0824 17.4777 12.8481 17.3642 12.574C17.2506 12.2999 17.2209 11.9983 17.2788 11.7074C17.3367 11.4164 17.4796 11.1491 17.6893 10.9393C17.8991 10.7296 18.1664 10.5867 18.4574 10.5288C18.7483 10.4709 19.0499 10.5006 19.324 10.6142C19.5981 10.7277 19.8324 10.92 19.9972 11.1666C20.162 11.4133 20.25 11.7033 20.25 12C20.25 12.3978 20.092 12.7794 19.8107 13.0607C19.5294 13.342 19.1478 13.5 18.75 13.5Z" fill="white" />
                                <path d="M5.25 13.5C4.95333 13.5 4.66332 13.412 4.41665 13.2472C4.16997 13.0824 3.97771 12.8481 3.86418 12.574C3.75065 12.2999 3.72094 11.9983 3.77882 11.7074C3.8367 11.4164 3.97956 11.1491 4.18934 10.9393C4.39912 10.7296 4.66639 10.5867 4.95737 10.5288C5.24834 10.4709 5.54994 10.5006 5.82403 10.6142C6.09812 10.7277 6.33238 10.92 6.49721 11.1666C6.66203 11.4133 6.75 11.7033 6.75 12C6.75 12.3978 6.59197 12.7794 6.31066 13.0607C6.02936 13.342 5.64783 13.5 5.25 13.5Z" fill="white" />
                            </svg>
                            <NavDropdown
                                show={showDropdown}
                                onToggle={handleToggle}
                                title=""
                                id="nav-dropdown"
                                align="end"
                                className="d-inline custom-nav-dropdown"
                                style={{ display: showDropdown ? 'block' : 'none' }}
                            >
                                <NavDropdown.Item className="dropdown-option" onClick={() => removeWidgetFromGrid('performance')}>Close Window</NavDropdown.Item>
                            </NavDropdown>
                        </div>
                    </div>
                    <ButtonGroup className="chart-buttons" style={{marginLeft: "6px"}}>
                        {['1D', '5D', '1M', '6M', 'YTD', '5Y', '10Y'].map((label) => (
                            <Button
                                key={label}
                                className={activeButton === label ? 'active ml-0' : ' ml-0'}
                                onClick={() => handleButtonClick(label)}
                            >
                                {label}
                            </Button>
                        ))}
                        {/* Dropdown button for additional ranges */}
                        <Dropdown as={ButtonGroup} onSelect={handleDropdownSelect}>
                            <Dropdown.Toggle
                                variant="secondary" // Adjust variant to match the design
                                className={activeButton === selectedDropdownOption ? 'active ml-0' : ' ml-0'} // Highlight if a range is selected
                            >
                                {selectedDropdownOption}
                            </Dropdown.Toggle>
                            <Dropdown.Menu className='preformance-dropdown'>
                                {['1M – 11M', '1Y – 10Y'].map((option, index) => (
                                    <Dropdown.Item key={index} eventKey={option}>
                                        {option}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                </Card.Header>
                <Card.Body className="pt-0">
                    <div className="d-flex mb-3">

                        <div>
                            <Button aria-describedby={id} variant="contained" className="dropdown-btn" onClick={handleClick}>
                            <span className="holdings">Holdings</span> <img src={`${process.env.PUBLIC_URL}/images/down 2.svg`} alt="icon" className="float-right mt-1" style={{marginLeft: "5px"}} />
                            </Button>
                            <Popover
                                id={id}
                                open={open}
                                anchorEl={anchorEl}
                                onClose={handleClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                style={{marginTop: "5px"}}
                            >
                                <Form className="p-2">
                                    {Object.keys(pnlResults?.pnl_results || {})
                                    .filter(symbol => symbol !== 'Core')
                                    .map((symbol, index) => (
                                        <div key={index} className="d-flex justify-content-between mb-2">
                                            <span className="ml-2">{symbol}</span>
                                            <Form.Check
                                                type="switch"
                                                id={`toggle-${symbol.toLowerCase()}`}
                                                checked={selectedSeries[index]} // Ensure index matches series array
                                                onChange={() => handleSeriesToggle(index)}
                                            />
                                        </div>
                                    ))}
                                </Form>
                            </Popover>
                        </div>

                        <CustomDropdown
                            options={['Line Chart', 'Bar Chart']}  // The options you want to display
                            selectedOption={chartOption}  // The currently selected option
                            onSelect={handleChartChange}   // Function to toggle the switch on/off
                        />  
                        

                    </div>

                    <div className="legend mt-3" style={{position: "absolute"}}>
                        {filteredSeries.map((series, index) => (
                            <>
                            <div key={index} className="legend-item">
                                <span
                                    className="legend-dot"
                                    style={{ backgroundColor: series.color }}
                                ></span>
                                <span className="legend-name">{series.name}</span>
                                <span className="legend-value" style={{ color: series.color, marginLeft: "8px" }}>
                                {series.data[series.data.length - 1]?.toFixed(2)}%
                                </span>
                            </div>
                            </>
                        ))}
                    </div>
                    
                    <Chart options={chartOptions} series={filteredSeries} type={chartType} height="85%" />
                </Card.Body>
            </Card>

            <SwitchAccount open={switchAccount} onClose={handleCloseSwitchAccount}/>
        </>
        
    );
};

export default Performance;
